import { create } from 'zustand'
import {
  Node,
  Edge,
  NodeChange,
  EdgeChange,
  applyNodeChanges as applyReactFlowNodeChanges,
  applyEdgeChanges as applyReactFlowEdgeChanges,
} from '@xyflow/react'
import { GraphNodeData, GraphEdge } from '@/types/graph'

interface HistoryState {
  nodes: Node<GraphNodeData>[]
  edges: GraphEdge[]
}

interface CanvasStore {
  nodes: Node<GraphNodeData>[]
  edges: GraphEdge[]
  selectedNodeId: string | null
  isDirty: boolean
  history: HistoryState[]
  historyIndex: number
  replaceGraph: (
    nodes: Node<GraphNodeData>[],
    edges: GraphEdge[],
  ) => void
  addNode: (node: Node<GraphNodeData>) => void
  removeNode: (id: string) => void
  updateNode: (id: string, data: Partial<GraphNodeData>) => void
  addEdge: (edge: GraphEdge) => void
  removeEdge: (id: string) => void
  applyNodeChanges: (changes: NodeChange[]) => void
  applyEdgeChanges: (changes: EdgeChange[]) => void
  setNodes: (nodes: Node<GraphNodeData>[]) => void
  setEdges: (edges: GraphEdge[]) => void
  setSelectedNodeId: (id: string | null) => void
  undo: () => void
  redo: () => void
  clearHistory: () => void
  markDirty: () => void
  markClean: () => void
  clear: () => void
}

function cloneGraphState(
  nodes: Node<GraphNodeData>[],
  edges: GraphEdge[],
): HistoryState {
  return {
    nodes: typeof structuredClone === 'function' ? structuredClone(nodes) : JSON.parse(JSON.stringify(nodes)),
    edges: typeof structuredClone === 'function' ? structuredClone(edges) : JSON.parse(JSON.stringify(edges)),
  }
}

const pushToHistory = (
  state: CanvasStore,
  nodes: Node<GraphNodeData>[],
  edges: GraphEdge[],
): CanvasStore => {
  const newHistory = state.history.slice(0, state.historyIndex + 1)
  newHistory.push(cloneGraphState(nodes, edges))
  return {
    ...state,
    nodes,
    edges,
    history: newHistory,
    historyIndex: newHistory.length - 1,
    isDirty: true,
  }
}

export const useCanvasStore = create<CanvasStore>((set) => ({
  nodes: [],
  edges: [],
  selectedNodeId: null,
  isDirty: false,
  history: [{ nodes: [], edges: [] }],
  historyIndex: 0,

  replaceGraph: (nodes, edges) =>
    set(() => ({
      nodes,
      edges,
      selectedNodeId: null,
      isDirty: false,
      history: [cloneGraphState(nodes, edges)],
      historyIndex: 0,
    })),

  addNode: (node) =>
    set((state) => {
      const nodes = [...state.nodes, node]
      return pushToHistory(state, nodes, state.edges)
    }),

  removeNode: (id) =>
    set((state) => {
      const nodes = state.nodes.filter((n) => n.id !== id)
      const edges = state.edges.filter((e) => e.source !== id && e.target !== id)
      return pushToHistory(state, nodes, edges)
    }),

  updateNode: (id, data) =>
    set((state) => {
      const nodes = state.nodes.map((n) =>
        n.id === id ? { ...n, data: { ...n.data, ...data } } : n,
      )
      return pushToHistory(state, nodes, state.edges)
    }),

  addEdge: (edge) =>
    set((state) => {
      const edges = [...state.edges, edge]
      return pushToHistory(state, state.nodes, edges)
    }),

  removeEdge: (id) =>
    set((state) => {
      const edges = state.edges.filter((e) => e.id !== id)
      return pushToHistory(state, state.nodes, edges)
    }),

  applyNodeChanges: (changes) =>
    set((state) => {
      const nodes = applyReactFlowNodeChanges(changes, state.nodes) as Node<GraphNodeData>[]
      return pushToHistory(state, nodes, state.edges)
    }),

  applyEdgeChanges: (changes) =>
    set((state) => {
      const edges = applyReactFlowEdgeChanges(changes, state.edges) as GraphEdge[]
      return pushToHistory(state, state.nodes, edges)
    }),

  setNodes: (nodes) => set({ nodes }),

  setEdges: (edges) => set({ edges }),

  setSelectedNodeId: (id) => set({ selectedNodeId: id }),

  undo: () =>
    set((state) => {
      if (state.historyIndex > 0) {
        const newIndex = state.historyIndex - 1
        const historyState = state.history[newIndex]
        return {
          ...state,
          nodes: historyState.nodes,
          edges: historyState.edges,
          historyIndex: newIndex,
          isDirty: true,
        }
      }
      return state
    }),

  redo: () =>
    set((state) => {
      if (state.historyIndex < state.history.length - 1) {
        const newIndex = state.historyIndex + 1
        const historyState = state.history[newIndex]
        return {
          ...state,
          nodes: historyState.nodes,
          edges: historyState.edges,
          historyIndex: newIndex,
          isDirty: true,
        }
      }
      return state
    }),

  clearHistory: () =>
    set((state) => ({
      ...state,
      history: [{ nodes: state.nodes, edges: state.edges }],
      historyIndex: 0,
    })),

  markDirty: () => set({ isDirty: true }),

  markClean: () => set({ isDirty: false }),

  clear: () =>
    set({
      nodes: [],
      edges: [],
      selectedNodeId: null,
      isDirty: false,
      history: [{ nodes: [], edges: [] }],
      historyIndex: 0,
    }),
}))
